/// <reference path="./string.d.ts" />
import { PageGen, PdfPen, PdfPoint, PdfSize } from '../pdf';
import { ETextAlign, PdfBrush, PdfFont, PdfRect } from '../pdf';
import {
  TextRenderingMode,
  pushGraphicsState,
  popGraphicsState,
  setTextRenderingMode,
  setStrokingColor,
  setLineWidth,
  setFillingColor,
  rgb,
} from 'pdf-lib';

PageGen.prototype.drawString = function (
  text: string,
  font: PdfFont,
  brush: PdfBrush | null,
  pen: PdfPen | null,
  rect: PdfRect,
  align: ETextAlign
): void {
  const f = this.pdf.getFont(font.font);

  // width and approximate ascent (height above baseline)
  const textWidth = f.widthOfTextAtSize(text, font.size);
  const textHeight = f.heightAtSize(font.size, { descender: true });
  const ascent = f.heightAtSize(font.size, { descender: false });

  // Layout-space (top-left origin)
  let x = rect.left;
  const yTop = rect.top;

  // Horizontal align
  switch (align) {
    case ETextAlign.TopCenter:
    case ETextAlign.Center:
    case ETextAlign.BottomCenter:
    case ETextAlign.CenterBaseline:
      x = rect.left + (rect.width - textWidth) / 2;
      break;
    case ETextAlign.TopRight:
    case ETextAlign.CenterRight:
    case ETextAlign.BottomRight:
    case ETextAlign.RightBaseline:
      x = rect.right - textWidth;
      break;
    // LeftBaseline: x = rect.left (default)
  }

  let yBaselineTL: number;

  switch (align) {
    case ETextAlign.CenterLeft:
    case ETextAlign.Center:
    case ETextAlign.CenterRight:
      yBaselineTL = rect.top + (rect.height - textHeight) / 2 + ascent;
      break;

    case ETextAlign.BottomLeft:
    case ETextAlign.BottomCenter:
    case ETextAlign.BottomRight:
      yBaselineTL = rect.bottom - (textHeight - ascent);
      break;

    case ETextAlign.LeftBaseline:
    case ETextAlign.CenterBaseline:
    case ETextAlign.RightBaseline:
      yBaselineTL = rect.bottom;
      break;

    default: // Top*
      yBaselineTL = rect.top + ascent;
      break;
  }

  // Convert to pdf-lib
  const pdfLibCoords = this.getXY(new PdfPoint(x, yBaselineTL));

  if (!brush && !pen) {
    return;
  }

  const textOptions: {
    x: number;
    y: number;
    size: number;
    font: typeof f;
    color: import('pdf-lib').Color;
    opacity: number;
  } = {
    x: pdfLibCoords.x,
    y: pdfLibCoords.y,
    size: font.size,
    font: f,
    color: brush ? brush.color.toPdfLibColor() : rgb(0, 0, 0),
    opacity: brush ? brush.color.opacity : 1,
  };

  if (pen) {
    this.page.pushOperators(
      pushGraphicsState(),
      setTextRenderingMode(brush ? TextRenderingMode.FillAndOutline : TextRenderingMode.Outline),
      setStrokingColor(pen.color.toPdfLibColor()),
      setLineWidth(pen.thickness)
    );
    textOptions.opacity = Math.min(textOptions.opacity, pen.color.opacity);
  }

  this.page.drawText(text, textOptions);

  if (pen) {
    this.page.pushOperators(popGraphicsState());
  }
};

PageGen.prototype.drawStringOutline = function (
  text: string,
  font: PdfFont,
  brush: PdfBrush,
  pen: PdfPen,
  rect: PdfRect,
  align: ETextAlign
): void {
  const f = this.pdf.getFont(font.font);

  const textWidth = f.widthOfTextAtSize(text, font.size);
  const textHeight = f.heightAtSize(font.size, { descender: true });
  const ascent = f.heightAtSize(font.size, { descender: false });

  let x = rect.left;

  switch (align) {
    case ETextAlign.TopCenter:
    case ETextAlign.Center:
    case ETextAlign.BottomCenter:
      x = rect.left + (rect.width - textWidth) / 2;
      break;
    case ETextAlign.TopRight:
    case ETextAlign.CenterRight:
    case ETextAlign.BottomRight:
      x = rect.right - textWidth;
      break;
  }

  let yBaselineTL: number;

  switch (align) {
    case ETextAlign.CenterLeft:
    case ETextAlign.Center:
    case ETextAlign.CenterRight:
      yBaselineTL = rect.top + (rect.height - textHeight) / 2 + ascent;
      break;

    case ETextAlign.BottomLeft:
    case ETextAlign.BottomCenter:
    case ETextAlign.BottomRight:
      yBaselineTL = rect.bottom - (textHeight - ascent);
      break;

    default:
      yBaselineTL = rect.top + ascent;
      break;
  }

  const pdfLibCoords = this.getXY(new PdfPoint(x, yBaselineTL));

  this.page.pushOperators(pushGraphicsState());

  this.page.pushOperators(
    setTextRenderingMode(TextRenderingMode.Outline),
    setStrokingColor(pen.color.toPdfLibColor()),
    setLineWidth(pen.thickness * 2)
  );

  this.page.drawText(text, {
    x: pdfLibCoords.x,
    y: pdfLibCoords.y,
    size: font.size,
    font: f,
    color: rgb(0, 0, 0),
    opacity: pen.color.opacity,
  });

  this.page.pushOperators(popGraphicsState());

  this.page.pushOperators(pushGraphicsState());

  this.page.pushOperators(
    setTextRenderingMode(TextRenderingMode.Fill),
    setFillingColor(brush.color.toPdfLibColor())
  );

  this.page.drawText(text, {
    x: pdfLibCoords.x,
    y: pdfLibCoords.y,
    size: font.size,
    font: f,
    color: brush.color.toPdfLibColor(),
    opacity: brush.color.opacity,
  });

  this.page.pushOperators(popGraphicsState());
};

PageGen.prototype.measureString = function (text: string, font: PdfFont): PdfSize {
  const f = this.pdf.getFont(font.font);

  this.pdf.pdf.embedFont;

  const textWidth = f.widthOfTextAtSize(text, font.size);
  const textHeight = f.heightAtSize(font.size, { descender: true }); // for some reason needed to get correct textHeight

  return new PdfSize(textWidth, textHeight * 1.2); // 1.2 is the approximate line spacing factor
};

PageGen.prototype.drawStrings = function (
  texts: string[],
  font: PdfFont,
  brush: PdfBrush | null,
  pen: PdfPen | null,
  rect: PdfRect,
  align: ETextAlign
): void {
  const f = this.adjustFont(rect.width, texts, font);
  const pdfFont = this.pdf.getFont(f.font);

  const sizes = texts.map((text) => {
    const textWidth = pdfFont.widthOfTextAtSize(text, f.size);
    return { width: textWidth, height: rect.height / texts.length };
  });

  const totalHeight = sizes.reduce((sum, s) => sum + s.height, 0);
  let top = rect.y + (rect.height - totalHeight) / 2;

  for (let i = 0; i < texts.length; i++) {
    const r = new PdfRect(rect.x, top, rect.width, sizes[i].height);
    this.drawString(texts[i], f, brush, pen, r, align);
    top += sizes[i].height;
  }
};

PageGen.prototype.adjustFont = function (
  maxWidth: number,
  texts: string[],
  font: PdfFont
): PdfFont {
  const f = this.pdf.getFont(font.font);
  const maxTextWidth = Math.max(...texts.map((text) => f.widthOfTextAtSize(text, font.size)));
  if (maxTextWidth <= 0) return font;
  const fontSize = Math.min(font.size * (maxWidth / maxTextWidth), font.size);

  return new PdfFont(font.font, fontSize);
};

function getLineHeight(fontSize: number): number {
  if (fontSize >= 6) return 1.2;
  if (fontSize >= 4) return 1.0;
  return 0.8;
}

function wrapWordsToFit(
  words: string[],
  f: any,
  fontSize: number,
  maxWidth: number,
  protectShortWords = false
): string[] {
  if (words.length === 0) return [''];
  if (words.length === 1) return words;

  const lines: string[] = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const testLine = `${currentLine  } ${  words[i]}`;
    const testWidth = f.widthOfTextAtSize(testLine, fontSize);
    if (testWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      lines.push(currentLine);
      currentLine = words[i];
    }
  }
  if (currentLine) lines.push(currentLine);

  if (protectShortWords && lines.length > 1) {
    const processed: string[] = [];
    for (let i = 0; i < lines.length; i++) {
      const lineWords = lines[i].split(' ');
      const lastWord = lineWords[lineWords.length - 1];

      if (lastWord.length === 1 && i < lines.length - 1) {
        lineWords.pop();
        if (lineWords.length > 0) {
          processed.push(lineWords.join(' '));
        }
        const nextLineWords = lines[i + 1].split(' ');
        nextLineWords.unshift(lastWord);
        lines[i + 1] = nextLineWords.join(' ');
      } else {
        processed.push(lines[i]);
      }
    }
    return processed;
  }

  return lines;
}

PageGen.prototype.measureStringMulti = function (
  text: string,
  font: PdfFont,
  size: PdfSize
): PdfSize {
  const f = this.pdf.getFont(font.font);
  const words = text.split(' ');
  const protectShortWords = text.length >= 12;

  const singleLineWidth = f.widthOfTextAtSize(text, font.size);
  if (singleLineWidth <= size.width) {
    const textHeight = f.heightAtSize(font.size, { descender: true });
    return new PdfSize(singleLineWidth, textHeight * 1.2);
  }

  const lines = wrapWordsToFit(words, f, font.size, size.width, protectShortWords);
  const lineHeight = getLineHeight(font.size) * font.size;
  const totalHeight = lines.length * lineHeight;
  const maxLineWidth = Math.max(...lines.map((line) => f.widthOfTextAtSize(line, font.size)));

  return new PdfSize(maxLineWidth, totalHeight);
};

PageGen.prototype.adjustFontMulti = function (
  texts: string[][],
  font: PdfFont,
  size: PdfSize
): PdfFont {
  let fontSize = font.size;
  const f = this.pdf.getFont(font.font);

  const MIN_FONT_SIZE = 1;

  while (fontSize > MIN_FONT_SIZE) {
    const lineHeight = getLineHeight(fontSize) * fontSize;
    let allSetsFit = true;

    for (const textSet of texts) {
      const setChars = textSet.reduce((sum, t) => sum + t.length, 0);
      const protectShortWords = textSet.length >= 2 || setChars >= 12;
      let setTotalLines = 0;

      for (const text of textSet) {
        const words = text.split(' ');
        const singleLineWidth = f.widthOfTextAtSize(text, fontSize);

        if (singleLineWidth <= size.width) {
          setTotalLines += 1;
        } else {
          const lines = wrapWordsToFit(words, f, fontSize, size.width, protectShortWords);
          for (const line of lines) {
            if (f.widthOfTextAtSize(line, fontSize) > size.width) {
              allSetsFit = false;
              break;
            }
          }
          setTotalLines += lines.length;
        }

        if (!allSetsFit) break;
      }

      if (!allSetsFit) break;

      const setTotalHeight = setTotalLines * lineHeight;
      if (setTotalHeight > size.height) {
        allSetsFit = false;
        break;
      }
    }

    if (allSetsFit) {
      return new PdfFont(font.font, fontSize);
    }

    fontSize -= 0.5;
  }

  return new PdfFont(font.font, fontSize);
};

PageGen.prototype.drawStringMulti = function (
  text: string,
  font: PdfFont,
  brush: PdfBrush | null,
  pen: PdfPen | null,
  rect: PdfRect,
  align: ETextAlign
): void {
  const fittedFont = this.adjustFontMulti([[text]], font, new PdfSize(rect.width, rect.height));
  const f = this.pdf.getFont(fittedFont.font);
  const words = text.split(' ');
  const protectShortWords = text.length >= 12;

  const singleLineWidth = f.widthOfTextAtSize(text, fittedFont.size);
  const lines: string[] = [];

  if (singleLineWidth <= rect.width) {
    lines.push(text);
  } else {
    const wrapped = wrapWordsToFit(words, f, fittedFont.size, rect.width, protectShortWords);
    for (const line of wrapped) {
      lines.push(line);
    }
  }

  const lineHeight = getLineHeight(fittedFont.size) * fittedFont.size;
  const totalHeight = lines.length * lineHeight;

  let startY: number;
  switch (align) {
    case ETextAlign.CenterLeft:
    case ETextAlign.Center:
    case ETextAlign.CenterRight:
      startY =
        rect.top +
        (rect.height - totalHeight) / 2 +
        f.heightAtSize(fittedFont.size, { descender: false });
      break;
    case ETextAlign.BottomLeft:
    case ETextAlign.BottomCenter:
    case ETextAlign.BottomRight:
      startY = rect.bottom - totalHeight + f.heightAtSize(fittedFont.size, { descender: false });
      break;
    default:
      startY = rect.top + f.heightAtSize(fittedFont.size, { descender: false });
  }

  if (!brush && !pen) {
    return;
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineWidth = f.widthOfTextAtSize(line, fittedFont.size);
    let x: number;

    switch (align) {
      case ETextAlign.TopCenter:
      case ETextAlign.Center:
      case ETextAlign.BottomCenter:
        x = rect.left + (rect.width - lineWidth) / 2;
        break;
      case ETextAlign.TopRight:
      case ETextAlign.CenterRight:
      case ETextAlign.BottomRight:
        x = rect.right - lineWidth;
        break;
      default:
        x = rect.left;
    }

    const y = startY + i * lineHeight;
    const pdfLibCoords = this.getXY(new PdfPoint(x, y));

    const textOptions: {
      x: number;
      y: number;
      size: number;
      font: typeof f;
      color: import('pdf-lib').Color;
      opacity: number;
    } = {
      x: pdfLibCoords.x,
      y: pdfLibCoords.y,
      size: fittedFont.size,
      font: f,
      color: brush ? brush.color.toPdfLibColor() : rgb(0, 0, 0),
      opacity: brush ? brush.color.opacity : 1,
    };

    if (pen) {
      this.page.pushOperators(
        pushGraphicsState(),
        setTextRenderingMode(brush ? TextRenderingMode.FillAndOutline : TextRenderingMode.Outline),
        setStrokingColor(pen.color.toPdfLibColor()),
        setLineWidth(pen.thickness)
      );
      textOptions.opacity = Math.min(textOptions.opacity, pen.color.opacity);
    }

    this.page.drawText(line, textOptions);

    if (pen) {
      this.page.pushOperators(popGraphicsState());
    }
  }
};

PageGen.prototype.drawStringsMulti = function (
  texts: string[],
  font: PdfFont,
  brush: PdfBrush | null,
  pen: PdfPen | null,
  rect: PdfRect,
  align: ETextAlign
): void {
  const fittedFont = this.adjustFontMulti([texts], font, new PdfSize(rect.width, rect.height));
  const f = this.pdf.getFont(fittedFont.font);
  const lineHeight = getLineHeight(fittedFont.size) * fittedFont.size;
  const totalChars = texts.reduce((sum, t) => sum + t.length, 0);
  const protectShortWords = texts.length >= 2 || totalChars >= 12;

  const textLines: { text: string; lines: string[] }[] = [];

  for (const text of texts) {
    const words = text.split(' ');
    const singleLineWidth = f.widthOfTextAtSize(text, fittedFont.size);

    if (singleLineWidth <= rect.width) {
      textLines.push({ text, lines: [text] });
    } else {
      const wrapped = wrapWordsToFit(words, f, fittedFont.size, rect.width, protectShortWords);
      textLines.push({ text, lines: wrapped });
    }
  }

  const totalLines = textLines.reduce((sum, tl) => sum + tl.lines.length, 0);
  const totalHeight = totalLines * lineHeight;

  let currentY: number;
  switch (align) {
    case ETextAlign.CenterLeft:
    case ETextAlign.Center:
    case ETextAlign.CenterRight:
      currentY = rect.top + (rect.height - totalHeight) / 2;
      break;
    case ETextAlign.BottomLeft:
    case ETextAlign.BottomCenter:
    case ETextAlign.BottomRight:
      currentY = rect.bottom - totalHeight;
      break;
    default:
      currentY = rect.top;
  }

  if (!brush && !pen) {
    return;
  }

  for (const tl of textLines) {
    const blockHeight = tl.lines.length * lineHeight;
    const blockTop = currentY + f.heightAtSize(fittedFont.size, { descender: false });

    for (let i = 0; i < tl.lines.length; i++) {
      const line = tl.lines[i];
      const lineWidth = f.widthOfTextAtSize(line, fittedFont.size);
      let x: number;

      switch (align) {
        case ETextAlign.TopCenter:
        case ETextAlign.Center:
        case ETextAlign.BottomCenter:
          x = rect.left + (rect.width - lineWidth) / 2;
          break;
        case ETextAlign.TopRight:
        case ETextAlign.CenterRight:
        case ETextAlign.BottomRight:
          x = rect.right - lineWidth;
          break;
        default:
          x = rect.left;
      }

      const y = blockTop + i * lineHeight;
      const pdfLibCoords = this.getXY(new PdfPoint(x, y));

      const textOptions: {
        x: number;
        y: number;
        size: number;
        font: typeof f;
        color: import('pdf-lib').Color;
        opacity: number;
      } = {
        x: pdfLibCoords.x,
        y: pdfLibCoords.y,
        size: fittedFont.size,
        font: f,
        color: brush ? brush.color.toPdfLibColor() : rgb(0, 0, 0),
        opacity: brush ? brush.color.opacity : 1,
      };

      if (pen) {
        this.page.pushOperators(
          pushGraphicsState(),
          setTextRenderingMode(
            brush ? TextRenderingMode.FillAndOutline : TextRenderingMode.Outline
          ),
          setStrokingColor(pen.color.toPdfLibColor()),
          setLineWidth(pen.thickness)
        );
        textOptions.opacity = Math.min(textOptions.opacity, pen.color.opacity);
      }

      this.page.drawText(line, textOptions);

      if (pen) {
        this.page.pushOperators(popGraphicsState());
      }
    }

    currentY += blockHeight;
  }
};
